import { sendEvent, GA_EVENTS } from '@shared'

const UTM_PARAM_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
] as const

export type UtmParams = Partial<Record<(typeof UTM_PARAM_KEYS)[number], string>>

export interface ParsedStartParam {
  referrerId: number | null
  utmParams: UtmParams
}

function parseQueryString(query: string): {
  ref: number | null
  utm: UtmParams
} {
  const utm: UtmParams = {}
  let ref: number | null = null

  const params = new URLSearchParams(query.replace(/^[?#]/, ''))
  const refVal = params.get('ref')
  if (refVal !== null) {
    const n = parseInt(refVal, 10)
    if (!isNaN(n)) ref = n
  }
  for (const key of UTM_PARAM_KEYS) {
    const val = params.get(key)
    if (val != null && val !== '') utm[key] = val
  }
  return { ref, utm }
}

/**
 * Парсит start_param (base64).
 * - Если "r_<number>" — текущий формат рефки.
 * - Иначе — query-строка: ref=123&utm_campaign=... (ref + utm_*).
 */
export function parseStartParam(
  startParam: string | undefined
): ParsedStartParam {
  const empty: ParsedStartParam = { referrerId: null, utmParams: {} }
  if (!startParam || !startParam.trim()) {
    return empty
  }

  try {
    const decoded = atob(startParam.trim())

    // Текущий формат: r_<telegramId>
    if (decoded.startsWith('r_')) {
      const telegramId = decoded.substring(2)
      const referrerId = parseInt(telegramId, 10)
      if (!isNaN(referrerId)) {
        return { referrerId, utmParams: {} }
      }
    }

    // Новый формат: query-строка ref=123&utm_campaign=...
    const { ref, utm } = parseQueryString(decoded)
    return {
      referrerId: ref,
      utmParams: utm,
    }
  } catch (error) {
    console.warn('[Referral] Failed to decode start param:', error)
    return empty
  }
}

/** Оставлено для обратной совместимости: из start_param достаёт только referrer id */
export const extractReferrerFromStartParam = (
  startParam: string | undefined
): number | null => {
  return parseStartParam(startParam).referrerId
}

/**
 * Генерирует реферальную ссылку.
 * Если передан utmParams — кодируется query-строка (ref=...&utm_campaign=...), иначе старый формат r_<id>.
 */
export function generateReferralLink(
  telegramId: string | number,
  utmParams?: UtmParams
): string {
  let encoded: string
  if (utmParams && Object.keys(utmParams).length > 0) {
    const params = new URLSearchParams()
    params.set('ref', String(telegramId))
    for (const key of UTM_PARAM_KEYS) {
      const val = utmParams[key]
      if (val != null && val !== '') params.set(key, val)
    }
    encoded = btoa(params.toString())
  } else {
    const referralCode = `r_${telegramId}`
    encoded = btoa(referralCode)
  }
  return `https://t.me/TheGlanceGameBot?startapp=${encoded}`
}

declare global {
  interface Window {
    dataLayer?: Record<string, unknown>[]
    gtag?: (
      command: 'event',
      eventName: string,
      params?: Record<string, unknown>
    ) => void
  }
}

/**
 * Подсовывает UTM в GTM: пушит в dataLayer и добавляет параметры в URL через replaceState.
 * GTM может читать либо из dataLayer (событие utm_params), либо из Page URL.
 * userCreatedAt — дата создания юзера (created_at), добавляется в параметры для GTM.
 */
export function applyUtmParamsForGtm(
  utmParams: UtmParams,
  userCreatedAt?: string
): void {
  if (!utmParams || Object.keys(utmParams).length === 0) return

  const gtmParams: Record<string, string> = { ...utmParams }
  if (userCreatedAt != null && userCreatedAt !== '') {
    gtmParams.created_at = userCreatedAt
  }

  const params = new URLSearchParams(
    typeof window !== 'undefined' ? window.location.search : ''
  )
  for (const [key, value] of Object.entries(gtmParams)) {
    if (value != null && value !== '') params.set(key, value)
  }
  const search = params.toString()
  if (search && typeof window !== 'undefined' && window.history?.replaceState) {
    const url = `${window.location.pathname}${search ? `?${search}` : ''}${
      window.location.hash || ''
    }`
    window.history.replaceState(null, '', url)
  }

  sendEvent(GA_EVENTS.UTM_PARAMS, gtmParams)
}
